import { addDays, addMonths, compareIsoDate, weekday, type IsoDate } from "./dates";
import type { TaskRecurrence } from "./models";

/**
 * Next due date for a recurring task after the occurrence due on `from`
 * is completed. If the task was overdue, we skip forward past `today` so a
 * week-late chore doesn't come back already overdue.
 */
export function nextDueDate(rule: TaskRecurrence, from: IsoDate, today: IsoDate): IsoDate {
  let next = step(rule, from);
  let guard = 0;
  while (compareIsoDate(next, today) <= 0 && guard++ < 1000) next = step(rule, next);
  return next;
}

function step(rule: TaskRecurrence, from: IsoDate): IsoDate {
  const interval = Math.max(1, Math.floor(rule.interval || 1));
  switch (rule.kind) {
    case "daily":
      return addDays(from, interval);
    case "monthly":
      return addMonths(from, interval);
    case "weekly": {
      const days = [...new Set(rule.weekdays)].filter((d) => d >= 0 && d <= 6).sort();
      if (days.length === 0) return addDays(from, 7 * interval);
      const wd = weekday(from);
      const laterThisWeek = days.find((d) => d > wd);
      if (laterThisWeek !== undefined) return addDays(from, laterThisWeek - wd);
      // Jump to the first listed weekday of the next active week.
      return addDays(from, 7 * interval - wd + days[0]!);
    }
  }
}

export function validateRecurrence(input: unknown): TaskRecurrence | null {
  if (input == null) return null;
  if (typeof input !== "object") throw new Error("Invalid recurrence");
  const r = input as Record<string, unknown>;
  const interval = Number.isInteger(r.interval) && (r.interval as number) >= 1 && (r.interval as number) <= 52 ? (r.interval as number) : 1;
  switch (r.kind) {
    case "daily":
      return { kind: "daily", interval };
    case "monthly":
      return { kind: "monthly", interval };
    case "weekly": {
      const weekdays = Array.isArray(r.weekdays)
        ? [...new Set(r.weekdays.filter((d): d is number => Number.isInteger(d) && d >= 0 && d <= 6))].sort()
        : [];
      return { kind: "weekly", interval, weekdays };
    }
    default:
      throw new Error("Invalid recurrence kind");
  }
}

export function describeRecurrence(rule: TaskRecurrence | null): string | null {
  if (!rule) return null;
  const every = (unit: string) => (rule.interval > 1 ? `Every ${rule.interval} ${unit}s` : `Every ${unit}`);
  switch (rule.kind) {
    case "daily":
      return rule.interval > 1 ? every("day") : "Daily";
    case "monthly":
      return rule.interval > 1 ? every("month") : "Monthly";
    case "weekly": {
      const names = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const days = rule.weekdays.map((d) => names[d]).join(", ");
      const base = rule.interval > 1 ? every("week") : "Weekly";
      return days ? `${base} · ${days}` : base;
    }
  }
}

/**
 * Whether a repeating thing (a routine) is on `date`, counting from `start`, its first day.
 * Daily: every `interval` days. Weekly: its weekdays (else start's) in every `interval`-th
 * week, weeks starting Sunday. Monthly: start's day of the month (the last day in shorter
 * months), every `interval` months.
 */
export function occursOn(rule: TaskRecurrence | null, start: IsoDate | null, date: IsoDate): boolean {
  if (start && compareIsoDate(date, start) < 0) return false;
  if (!rule) return true;
  const from = start ?? date;
  const interval = Math.max(1, Math.floor(rule.interval || 1));
  const daysBetween = (a: IsoDate, b: IsoDate) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
  switch (rule.kind) {
    case "daily":
      return daysBetween(from, date) % interval === 0;
    case "weekly": {
      const days = rule.weekdays.length ? rule.weekdays : [weekday(from)];
      if (!days.includes(weekday(date))) return false;
      const weeks = daysBetween(addDays(from, -weekday(from)), addDays(date, -weekday(date))) / 7;
      return weeks % interval === 0;
    }
    case "monthly": {
      const [fy, fm, fd] = from.split("-").map(Number) as [number, number, number];
      const [y, m, d] = date.split("-").map(Number) as [number, number, number];
      const months = (y - fy) * 12 + (m - fm);
      const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
      return months % interval === 0 && d === Math.min(fd, lastDay);
    }
  }
}
